"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@/modules/auth/context/UserContext";
import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { usePage } from "@/modules/pages/context/PageContext";
import { gtagConfig, gtagSetUserProperties } from "../lib/gtag";
import { firePageView } from "../events";
import type { PageViewEventParams } from "../types";
import type { IUser } from "@/modules/auth/types";
import type { Page, PageTab, ProductsFilter } from "@/modules/pages/types";
import type { Dealer, GetDealerContractedProductsResponse } from "@/modules/dealer/types";
import type { DateRange } from "@/modules/dashboard/types";

interface BuildPayloadArgs {
  user: IUser;
  currentPage: Page;
  currentTab: PageTab | null;
  selectedDealer: Dealer | null;
  dealerContractedProducts: GetDealerContractedProductsResponse | null;
  selectedDateRange: DateRange | null;
}

function serializeProductsFilter(
  filter: ProductsFilter | undefined
): string | undefined {
  if (!filter) return undefined;
  if (Array.isArray(filter)) return `legacy:${filter.join(",")}`;
  if ("$some" in filter) return `some:${filter.$some.join(",")}`;
  if ("$every" in filter) return `every:${filter.$every.join(",")}`;
  return undefined;
}

function toIsoDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

function buildPageViewPayload({
  user,
  currentPage,
  currentTab,
  selectedDealer,
  dealerContractedProducts,
  selectedDateRange,
}: BuildPayloadArgs): PageViewEventParams {
  const widgets = currentPage.widgets ?? [];
  const uniqueWidgetTypes = [...new Set(widgets.map((w) => w.type))];
  const userRoles = user.roles?.map((r) => r.name).join(",") ?? "";

  const activeProductsFilter =
    currentTab?.metadata?.products ?? currentPage.metadata?.products;

  return {
    page_title: currentPage.title,
    page_location:
      typeof window !== "undefined" ? window.location.href : "",
    page_view_source: "tracker",
    page_id: String(currentPage.id),
    page_dashboard_id: currentPage.dashboardId,
    user_id: user.userId,
    dealer_id: selectedDealer ? selectedDealer.dealerRefId : undefined,
    dealer_name: selectedDealer ? selectedDealer.dealerName : undefined
  };
}

export function PageViewTracker(): null {
  const { user } = useAuth();
  const { state: dashboardState } = useDashboard();
  const { state: pageState } = usePage();
  const alreadyConfigured = useRef(false);
  const { currentPage, currentTab } = pageState;
  const { selectedDealer, dealerContractedProducts, selectedDateRange } =
    dashboardState;

  const lastFiredKey = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.userId) return;
    const gaId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!gaId) return;
    if (alreadyConfigured.current) return;
    gtagConfig(gaId, { user_id: user.userId, send_page_view: false });
    alreadyConfigured.current = true;
  }, [user?.userId]);

  useEffect(() => {
    gtagSetUserProperties({
      dealer_id: selectedDealer ? selectedDealer.dealerRefId : null,
      dealer_name: selectedDealer?.dealerName ?? null,
    });
  }, [selectedDealer]);

  useEffect(() => {
    if (!user || !currentPage) return;

    // Wait until currentTab has settled for this page before firing.
    // currentTab may hold a stale value from the previous page between the
    // render where currentPage changes and the render where DashboardPageGrid
    // resets/sets the tab — firing during that window causes a double event.
    if (currentTab && currentTab.pageId !== currentPage.id) return; // stale tab — wait for reset
    const hasTabs = (currentPage.tabs?.length ?? 0) > 0;
    if (hasTabs && !currentTab) return; // tab page, but tab not yet selected — wait

    const key = [
      user.userId,
      currentPage.id,
      selectedDealer?.id ?? "",
    ].join("|");

    if (lastFiredKey.current === key) return;

    const timer = setTimeout(() => {
      if (lastFiredKey.current === key) return;
      const payload = buildPageViewPayload({
        user,
        currentPage,
        currentTab,
        selectedDealer,
        dealerContractedProducts,
        selectedDateRange,
      });
      firePageView(payload);
      lastFiredKey.current = key;
    }, 100);

    return () => clearTimeout(timer);
  }, [
    user,
    currentPage,
    selectedDealer,
    dealerContractedProducts,
    selectedDateRange,
  ]);

  return null;
}
